import React from "react";
import { useAuth } from "@clerk/clerk-react";

import { fetchFriendExchangeHistory, Recommendation } from "../api/albums";

export function useFriendExchangeTabData(
    friendExternalId: string | null | undefined,
    enabled: boolean
) {
    const { getToken } = useAuth();

    const [received, setReceived] = React.useState<Recommendation[]>([]);
    const [sent, setSent] = React.useState<Recommendation[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const hasLoadedRef = React.useRef(false);

    const refresh = React.useCallback(
        async (options?: { showLoading?: boolean }) => {
            if (!enabled || !friendExternalId) {
                setReceived([]);
                setSent([]);
                hasLoadedRef.current = false;
                setIsLoading(false);
                return;
            }

            const showLoading = options?.showLoading ?? !hasLoadedRef.current;
            if (showLoading) {
                setIsLoading(true);
            }

            try {
                const token = await getToken();
                const data = await fetchFriendExchangeHistory(friendExternalId, token);
                setReceived(data.received);
                setSent(data.sent);
                hasLoadedRef.current = true;
            } catch (error) {
                console.error("Error loading friend exchange history:", error);
                setReceived([]);
                setSent([]);
            } finally {
                setIsLoading(false);
            }
        },
        [enabled, friendExternalId, getToken]
    );

    React.useEffect(() => {
        setReceived([]);
        setSent([]);
        hasLoadedRef.current = false;
    }, [friendExternalId, enabled]);

    React.useEffect(() => {
        refresh();
    }, [refresh]);

    return {
        received,
        sent,
        isLoading,
        refresh,
    };
}
